import { useCallback, useEffect, useState } from 'react'
import { useGetTopics } from '@/hooks/api/dashboard'
import { TopicType } from '@/utils/constants'
import { topicToAlbumSummary } from '../mappers'
import { AlbumSummary } from '../types'

/**
 * Build albumId → galleryId map from the user's gallery topics.
 * Each gallery topic we create stores { sourceAlbumId } in topicXData.
 */
function buildGalleryMap(galleryTopics: Array<{ topicID: string; topicXData?: string }>): Record<string, string> {
  const map: Record<string, string> = {}
  for (const t of galleryTopics) {
    if (!t.topicXData) continue
    try {
      const parsed = JSON.parse(t.topicXData)
      if (parsed?.sourceAlbumId) {
        map[String(parsed.sourceAlbumId)] = t.topicID
      }
    } catch {
      /* ignore malformed xdata */
    }
  }
  return map
}

/** Load current user's albums and any linked 3D galleries */
export function useAlbumLibrary() {
  const getTopics = useGetTopics()
  const [albums, setAlbums] = useState<AlbumSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [albumTopics, galleryTopics] = await Promise.all([
        getTopics.mutateAsync({ pageNo: 1, interaction: 3, topicType: TopicType.ALBUM }),
        getTopics.mutateAsync({ pageNo: 1, interaction: 3, topicType: TopicType.GALLERY }),
      ])

      const galleryMap = buildGalleryMap(galleryTopics || [])

      const list: AlbumSummary[] = (albumTopics || [])
        .filter((t: { topicType?: string }) => String(t.topicType) === String(TopicType.ALBUM))
        .map((t: { topicID: string }) => ({
          ...topicToAlbumSummary(t as Parameters<typeof topicToAlbumSummary>[0]),
          galleryId: galleryMap[t.topicID],
        }))

      setAlbums(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load albums')
      setAlbums([])
    } finally {
      setLoading(false)
    }
  }, [getTopics])

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { albums, loading, error, refresh }
}
